import { clampTo, extractImageFrames } from '../../tools';
import type { WavespeedModelConfig } from '../types';

export const bytedanceConfigs: Record<string, WavespeedModelConfig> = {
    'seedance-2.0-uncensored': {
        // https://wavespeed.ai/zh-CN/models/bytedance/seedance-2.0/text-to-video
        // https://wavespeed.ai/zh-CN/models/bytedance/seedance-2.0/image-to-video
        apiPath: (params) => {
            const { frameMode } = params;
            if (frameMode === 'first' || frameMode === 'first_last') {
                return '/bytedance/seedance-2.0/image-to-video';
            } else {
                return '/bytedance/seedance-2.0/text-to-video';
            }
        },
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, aspectRatio, duration, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            const allMedia = [
                ...((videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []).filter(Boolean).map(url => ({ type: 'video' as const, url })),
                ...((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).map(url => ({ type: 'audio' as const, url })),
            ].slice(0, 3);
            const videos = allMedia.filter(m => m.type === 'video').map(m => m.url);
            const audios = allMedia.filter(m => m.type === 'audio').map(m => m.url);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['21:9', '16:9', '9:16', '4:3', '3:4', '1:1'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo(quality || '480p', ['480p', '720p', '1080p', '4k'] as const, '480p'),
                    // 首帧 URL
                    image: firstImage,
                    // 可选参数
                    enable_web_search: typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search : false,
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['21:9', '16:9', '9:16', '4:3', '3:4', '1:1'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo(quality || '480p', ['480p', '720p', '1080p', '4k'] as const, '480p'),
                    // 首帧 URL
                    image: firstImage,
                    // 尾帧 URL
                    last_image: lastImage,
                    // 可选参数
                    enable_web_search: typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search : false,
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['21:9', '16:9', '9:16', '4:3', '3:4', '1:1'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo(quality || '480p', ['480p', '720p', '1080p', '4k'] as const, '480p'),
                    // 参考 URL
                    reference_images: images,
                    reference_videos: videos,
                    reference_audios: audios,
                    // 可选参数
                    enable_web_search: typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search : false,
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                };
            // 文生视频模式
            } else {
                return {
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['21:9', '16:9', '9:16', '4:3', '3:4', '1:1'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo(quality || '480p', ['480p', '720p', '1080p', '4k'] as const, '480p'),
                    // 参考 URL
                    reference_images: [],
                    reference_videos: [],
                    reference_audios: [],
                    // 可选参数
                    enable_web_search: typeof providerOptions?.web_search === 'boolean' ? providerOptions.web_search : false,
                    generate_audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                };
            }
        },
    },
    'avatar-omni-human-1.5': {
        // https://wavespeed.ai/zh-CN/models/bytedance/avatar-omni-human-1.5
        apiPath: '/bytedance/avatar-omni-human-1.5',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { imagePrompt, audioUri } = params;
            const [firstImage] = extractImageFrames(imagePrompt);
            return {
                // 图片 URL
                image: firstImage,
                // 音频 URL
                audio: audioUri,
            };
        },
    },
};